/**
 * A dead session must leave the signed-in shell for /login.
 * FailureState used to rewrite a 401 into a "Sign in again" card, and the
 * query cache toasted the API's "Valid Bearer token or API key required".
 * 403 is a permission denial, not an expired session — leave it alone.
 *
 * The sessionStorage keys are shared with packages/api-client (client-runtime)
 * and the generated admin fetch. Rename them in all three places.
 */

const REAUTH_FLAG = 'inferrex.reauth';
const REAUTH_AT = 'inferrex.reauthAt';
/** After one bounce, hold further redirects so a still-rejected token cannot loop Microsoft. */
const REAUTH_WINDOW_MS = 90_000;

export function isSessionExpiredMessage(message: string): boolean {
  const raw = message.trim();
  if (!raw) return false;
  const lower = raw.toLowerCase();
  if (/\b403\b/.test(raw) || lower.includes('forbidden') || lower.includes('permission denied')) {
    return false;
  }
  if (/\b401\b/.test(raw)) return true;
  if (lower.includes('unauthorized')) return true;
  if (lower.includes('valid bearer token')) return true;
  return false;
}

export function isSessionExpiredError(error: unknown): boolean {
  if (typeof error === 'string') return isSessionExpiredMessage(error);
  if (!error || typeof error !== 'object') return false;
  const status = (error as { status?: unknown }).status;
  if (status === 401) return true;
  if (status === 403) return false;
  const message = error instanceof Error
    ? error.message
    : typeof (error as { message?: unknown }).message === 'string'
      ? (error as { message: string }).message
      : '';
  return isSessionExpiredMessage(message);
}

function onLoginPath(): boolean {
  const path = window.location.pathname;
  return path === '/login' || path.startsWith('/login/');
}

/**
 * Start a full navigation to /login. Returns false when already there, or when
 * a bounce already happened in the last 90s (the token is still being rejected —
 * another redirect would loop the Entra account picker). Returns true when this
 * document is already navigating, so callers keep the shell blank instead of
 * painting the failure card.
 */
export function redirectToLogin(): boolean {
  if (typeof window === 'undefined') return false;
  if (onLoginPath()) return false;
  const doc = window as Window & { __inferrexRedirecting?: boolean };
  if (doc.__inferrexRedirecting) return true;
  try {
    const at = Number(sessionStorage.getItem(REAUTH_AT) || '0');
    if (at && Date.now() - at < REAUTH_WINDOW_MS) return false;
    sessionStorage.setItem(REAUTH_FLAG, '1');
    sessionStorage.setItem(REAUTH_AT, String(Date.now()));
  } catch {
    // Private mode — still navigate.
  }
  doc.__inferrexRedirecting = true;
  window.location.replace('/login');
  return true;
}

/** A successful authenticated call means the session stuck; a later expiry may redirect. */
export function clearReauthGuard(): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(REAUTH_FLAG);
    sessionStorage.removeItem(REAUTH_AT);
  } catch {
    // ignore
  }
}
